/**
 * RatingScale Screen
 *
 * Editable table where the user defines the credit rating tiers used
 * throughout the analysis (e.g., AA, A, BBB, NIG/NR) and each tier's
 * concentration limit percentage. These values feed into:
 *  - BuyerRatings: the available ratings shown as drag-and-drop pills.
 *  - ConcentrationLimits: the % cap applied per buyer based on their rating.
 *
 * Changes are immediately dispatched to Redux (SET_RATING_SCALE) and
 * auto-saved to the backend via the project slice's save middleware.
 * Row order matters: the last rating is used as the default for unrated buyers.
 */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

const RatingScale = () => {
  const dispatch = useDispatch();
  const { ratingScale } = useSelector((state) => state.project);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (ratingScale) {
      setRows(JSON.parse(JSON.stringify(ratingScale)));
    }
  }, [ratingScale]);

  const save = (updated) => {
    dispatch({ type: "SET_RATING_SCALE", ratingScale: updated });
  };

  const updateRow = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
    save(updated);
  };

  const addRow = () => {
    const updated = [...rows, { rating: "", concLimitPct: 0 }];
    setRows(updated);
    save(updated);
  };

  const removeRow = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
    save(updated);
  };

  return (
    <div>
      <div className="box">
        <h2 className="subtitle">Rating Scale</h2>
        <table className="table is-fullwidth is-bordered is-striped">
          <thead>
            <tr>
              <th>Rating</th>
              <th>Conc. Limit %</th>
              <th style={{ width: "60px" }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>
                  <input
                    className="input is-small"
                    type="text"
                    value={row.rating}
                    onChange={(e) => updateRow(index, "rating", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="input is-small"
                    type="number"
                    step="0.01"
                    value={row.concLimitPct}
                    onChange={(e) =>
                      updateRow(index, "concLimitPct", parseFloat(e.target.value) || 0)
                    }
                  />
                </td>
                <td>
                  <button
                    className="button is-small is-danger is-light"
                    onClick={() => removeRow(index)}
                  >
                    <span className="icon">
                      <i className="fa-duotone fa-trash" />
                    </span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="button is-small" onClick={addRow}>
          <span className="icon">
            <i className="fa-duotone fa-plus" />
          </span>
          <span>Add Rating</span>
        </button>
      </div>
    </div>
  );
};

export default RatingScale;
